import { useState, useEffect } from 'react'
import axios from 'axios'
import { createClient } from '@supabase/supabase-js'
import SalaClaseVivo from './SalaClaseVivo'

const API = process.env.REACT_APP_API_URL

// Mismo patron que en el panel de admin: el navegador sube el archivo
// directamente a Supabase Storage (bucket "fotos-profesores") y solo
// la URL publica resultante se guarda en la base de datos.
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

async function subirFotoProfesor(archivo) {
  const ext = (archivo.name?.split('.').pop() || 'jpg').toLowerCase()
  const nombreArchivo = 'profesor_' + Date.now() + '_' + Math.random().toString(36).slice(2) + '.' + ext
  const { error } = await supabase.storage
    .from('fotos-profesores')
    .upload(nombreArchivo, archivo, { contentType: archivo.type })
  if (error) throw error
  const { data } = supabase.storage.from('fotos-profesores').getPublicUrl(nombreArchivo)
  return data.publicUrl
}

const DIAS = ['Domingo','Lunes','Martes','Miercoles','Jueves','Viernes','Sabado']
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

function formatFecha(fechaStr) {
  const d = new Date(fechaStr + 'T00:00:00')
  return DIAS[d.getDay()] + ' ' + d.getDate() + ' de ' + MESES[d.getMonth()]
}

function formatHora(horaStr) {
  if (!horaStr) return ''
  const [h, m] = horaStr.split(':')
  const hora = parseInt(h)
  const ampm = hora >= 12 ? 'PM' : 'AM'
  const h12 = hora % 12 || 12
  return h12 + ':' + m + ' ' + ampm
}

function esHoy(fechaStr) {
  const hoy = new Date().toISOString().split('T')[0]
  return fechaStr === hoy
}

function esFuturo(fechaStr) {
  const hoy = new Date().toISOString().split('T')[0]
  return fechaStr >= hoy
}

export default function PanelProfesor({ profesor, onLogout }) {
  const [perfil,       setPerfil]       = useState(profesor) // copia local editable (foto_url, bio)
  const [clases,       setClases]       = useState([])
  const [claseActiva,  setClaseActiva]  = useState(null)
  const [estudiantes,  setEstudiantes]  = useState([])
  const [materiales,   setMateriales]   = useState([])
  const [seccion,      setSeccion]      = useState('horario')
  const [cargando,     setCargando]     = useState(true)
  const [modal,        setModal]        = useState(null)
  const [form,         setForm]         = useState({})
  const [guardando,    setGuardando]    = useState(false)
  const [mensaje,      setMensaje]      = useState(null)
  const [claseEnVideo, setClaseEnVideo] = useState(null) // id de la clase cuya videollamada esta abierta

  useEffect(() => { cargarClases() }, [])

  async function cargarClases() {
    setCargando(true)
    try {
      const res = await axios.get(API + '/curriculo/profesor/' + profesor.id + '/clases')
      setClases(res.data.datos || [])
    } catch (err) { console.error(err) }
    finally { setCargando(false) }
  }

  async function abrirClase(clase) {
    setClaseActiva(clase)
    setSeccion('detalle')
    try {
      const [estRes, matRes] = await Promise.all([
        axios.get(API + '/curriculo/profesor/clase/' + clase.id + '/estudiantes'),
        axios.get(API + '/curriculo/profesor/clase/' + clase.id + '/materiales')
      ])
      setEstudiantes(estRes.data.datos || [])
      setMateriales(matRes.data.datos || [])
    } catch (err) { console.error(err) }
  }

  async function guardarMaterial() {
    setGuardando(true)
    try {
      await axios.post(API + '/curriculo/profesor/clase/' + claseActiva.id + '/materiales', {
        ...form, profesor_id: profesor.id
      })
      setMensaje({ tipo:'ok', texto:'Material agregado correctamente' })
      setTimeout(() => setMensaje(null), 3000)
      setModal(null); setForm({})
      const res = await axios.get(API + '/curriculo/profesor/clase/' + claseActiva.id + '/materiales')
      setMateriales(res.data.datos || [])
    } catch (err) {
      setMensaje({ tipo:'error', texto:'Error al guardar' })
    } finally { setGuardando(false) }
  }

  async function guardarNota() {
    if (!form.estudiante_id || !form.nota) return
    setGuardando(true)
    try {
      await axios.post(API + '/curriculo/profesor/clase/' + claseActiva.id + '/notas', {
        ...form, profesor_id: profesor.id
      })
      setMensaje({ tipo:'ok', texto:'Nota guardada correctamente' })
      setTimeout(() => setMensaje(null), 3000)
      setModal(null); setForm({})
    } catch (err) {
      setMensaje({ tipo:'error', texto:'Error al guardar nota' })
    } finally { setGuardando(false) }
  }

  const clasesHoy    = clases.filter(c => esHoy(c.fecha))
  const clasesFuturo = clases.filter(c => esFuturo(c.fecha) && !esHoy(c.fecha))
  const clasesPasado = clases.filter(c => !esFuturo(c.fecha) && !esHoy(c.fecha))

  const inputStyle = {
    width:'100%', padding:'9px 12px', border:'1.5px solid #E2E6F0',
    borderRadius:8, fontFamily:'inherit', fontSize:14, outline:'none',
    boxSizing:'border-box', marginBottom:12
  }
  const labelStyle = { fontSize:12, fontWeight:700, color:'#374151', marginBottom:4, display:'block' }

  // La sala de video toma toda la pantalla, sin el encabezado del panel.
  if (claseEnVideo) {
    return <SalaClaseVivo profesorId={profesor.id} claseId={claseEnVideo} onSalir={() => setClaseEnVideo(null)} />
  }

  return (
    <div style={{ minHeight:'100vh', background:'#F4F6FA', fontFamily:'system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#065F46,#059669)', padding:'16px 32px', color:'#fff', display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:24, fontWeight:900 }}>
          <span style={{ color:'#FFD700' }}>Esp</span>
          <span style={{ color:'#fff' }}>añ</span>
          <span style={{ color:'#FCD34D' }}>olGo</span>
        </div>
        <div style={{ width:1, height:28, background:'rgba(255,255,255,0.3)' }} />
        <div style={{ fontSize:14, fontWeight:600 }}>Panel Profesor</div>
        <div style={{ flex:1 }} />
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:800, overflow:'hidden' }}>
            {perfil.foto_url ? <img src={perfil.foto_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : profesor.nombre?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize:13, fontWeight:700 }}>{profesor.nombre} {profesor.apellido}</div>
            <div style={{ fontSize:11, opacity:.8 }}>{profesor.especialidad}</div>
          </div>
          <button onClick={onLogout}
            style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:8, padding:'6px 14px', color:'#fff', fontFamily:'inherit', fontSize:12, cursor:'pointer', marginLeft:8 }}>
            Cerrar sesion
          </button>
        </div>
      </div>

      <div style={{ maxWidth:1000, margin:'0 auto', padding:28 }}>

        {mensaje && (
          <div style={{ padding:'12px 16px', borderRadius:10, marginBottom:16, fontSize:14, fontWeight:600,
            background: mensaje.tipo==='ok' ? '#F0FDF4' : '#FEF2F2',
            color: mensaje.tipo==='ok' ? '#166534' : '#991B1B',
            border:'1px solid ' + (mensaje.tipo==='ok' ? '#22C55E' : '#EF4444') }}>
            {mensaje.tipo==='ok' ? '✅' : '❌'} {mensaje.texto}
          </div>
        )}

        {/* Nav */}
        <div style={{ display:'flex', gap:8, marginBottom:24 }}>
          {[
            { id:'horario', ic:'📅', label:'Mi horario'    },
            { id:'detalle', ic:'🎥', label:'Detalle clase', disabled: !claseActiva },
            { id:'perfil',  ic:'👤', label:'Mi perfil'      },
          ].map(item => (
            <button key={item.id}
              onClick={() => !item.disabled && setSeccion(item.id)}
              disabled={item.disabled}
              style={{ padding:'8px 18px', borderRadius:10, border:'2px solid ' + (seccion===item.id ? '#059669' : '#E2E6F0'),
                background: seccion===item.id ? '#059669' : '#fff',
                color: seccion===item.id ? '#fff' : item.disabled ? '#9CA3AF' : '#374151',
                fontFamily:'inherit', fontSize:13, fontWeight:600,
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                display:'flex', alignItems:'center', gap:6, opacity: item.disabled ? 0.5 : 1 }}>
              <span>{item.ic}</span><span>{item.label}</span>
            </button>
          ))}
        </div>

        {/* HORARIO */}
        {seccion === 'horario' && (
          <div>
            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:28 }}>
              {[
                { ic:'📅', val: clases.length,      label:'Total clases',       color:'#059669' },
                { ic:'🔥', val: clasesHoy.length,   label:'Clases hoy',         color:'#EF4444' },
                { ic:'⏳', val: clasesFuturo.length, label:'Proximas clases',    color:'#3B82F6' },
                { ic:'✅', val: clasesPasado.length, label:'Clases realizadas',  color:'#6B7280' },
              ].map((s,i) => (
                <div key={i} style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:'18px 16px', textAlign:'center' }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>{s.ic}</div>
                  <div style={{ fontSize:28, fontWeight:900, color:s.color }}>{s.val}</div>
                  <div style={{ fontSize:12, color:'#6B7280', fontWeight:600 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Clases de hoy */}
            {clasesHoy.length > 0 && (
              <div style={{ marginBottom:28 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                  <div style={{ background:'#EF4444', color:'#fff', borderRadius:10, padding:'6px 14px', fontSize:13, fontWeight:700 }}>
                    🔥 HOY
                  </div>
                  <div style={{ flex:1, height:1, background:'#E2E6F0' }} />
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {clasesHoy.map(clase => (
                    <TarjetaClase key={clase.id} clase={clase} hoy={true} onClick={() => abrirClase(clase)} />
                  ))}
                </div>
              </div>
            )}

            {/* Proximas clases */}
            {clasesFuturo.length > 0 && (
              <div style={{ marginBottom:28 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                  <div style={{ background:'#3B82F6', color:'#fff', borderRadius:10, padding:'6px 14px', fontSize:13, fontWeight:700 }}>
                    📅 Proximas clases
                  </div>
                  <div style={{ flex:1, height:1, background:'#E2E6F0' }} />
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {clasesFuturo.map(clase => (
                    <TarjetaClase key={clase.id} clase={clase} hoy={false} onClick={() => abrirClase(clase)} />
                  ))}
                </div>
              </div>
            )}

            {/* Clases pasadas */}
            {clasesPasado.length > 0 && (
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                  <div style={{ background:'#6B7280', color:'#fff', borderRadius:10, padding:'6px 14px', fontSize:13, fontWeight:700 }}>
                    ✅ Clases realizadas
                  </div>
                  <div style={{ flex:1, height:1, background:'#E2E6F0' }} />
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10, opacity:.7 }}>
                  {clasesPasado.map(clase => (
                    <TarjetaClase key={clase.id} clase={clase} hoy={false} onClick={() => abrirClase(clase)} />
                  ))}
                </div>
              </div>
            )}

            {clases.length === 0 && !cargando && (
              <div style={{ textAlign:'center', padding:60, color:'#6B7280' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
                <p>No tienes clases programadas aun.</p>
              </div>
            )}
          </div>
        )}

        {/* MI PERFIL */}
        {seccion === 'perfil' && (
          <TarjetaMiPerfil perfil={perfil} onActualizado={datos => setPerfil(p => ({ ...p, ...datos }))} />
        )}

        {/* DETALLE CLASE */}
        {seccion === 'detalle' && claseActiva && (
          <div>
            <button onClick={() => setSeccion('horario')}
              style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', fontFamily:'inherit', marginBottom:16 }}>
              ← Volver al horario
            </button>

            {/* Info clase */}
            <div style={{ background:'linear-gradient(135deg,#065F46,#059669)', borderRadius:16, padding:24, color:'#fff', marginBottom:20 }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:16 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, opacity:.8, marginBottom:4 }}>
                    {formatFecha(claseActiva.fecha)} · {formatHora(claseActiva.hora_inicio)} - {formatHora(claseActiva.hora_fin)}
                  </div>
                  <h2 style={{ fontSize:20, fontWeight:800, marginBottom:6 }}>{claseActiva.nombre}</h2>
                  {claseActiva.tema && <div style={{ fontSize:13, opacity:.85 }}>Tema: {claseActiva.tema}</div>}
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:28, fontWeight:900 }}>{estudiantes.length}</div>
                  <div style={{ fontSize:12, opacity:.8 }}>estudiantes</div>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:14, flexWrap:'wrap' }}>
                <button onClick={() => setClaseEnVideo(claseActiva.id)}
                  style={{ background:'#fff', color:'#065F46', border:'none', borderRadius:10, padding:'8px 18px', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                  🎥 Entrar a la sala de video
                </button>
                {claseActiva.link_clase && (
                  <a href={claseActiva.link_clase} target='_blank' rel='noopener noreferrer'
                    style={{ display:'inline-block', background:'rgba(255,255,255,0.2)', color:'#fff', borderRadius:10, padding:'8px 18px', fontSize:13, fontWeight:600, textDecoration:'none' }}>
                    🔗 Abrir enlace externo
                  </a>
                )}
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

              {/* Estudiantes */}
              <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <h3 style={{ fontSize:15, fontWeight:700 }}>👥 Estudiantes ({estudiantes.length})</h3>
                  <button onClick={() => setModal('nota')}
                    style={{ background:'#059669', color:'#fff', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    + Nota
                  </button>
                </div>
                {estudiantes.length === 0 ? (
                  <div style={{ textAlign:'center', padding:20, color:'#9CA3AF', fontSize:13 }}>Sin estudiantes inscritos</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {estudiantes.map((e, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#F4F6FA', borderRadius:8 }}>
                        <div style={{ width:32, height:32, borderRadius:'50%', background:'#059669', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff' }}>
                          {e.usuarios?.email?.[0]?.toUpperCase()}
                        </div>
                        <div style={{ fontSize:13, color:'#374151' }}>{e.usuarios?.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Materiales */}
              <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <h3 style={{ fontSize:15, fontWeight:700 }}>📎 Materiales ({materiales.length})</h3>
                  <button onClick={() => { setForm({ tipo:'nota' }); setModal('material') }}
                    style={{ background:'#3B82F6', color:'#fff', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                    + Material
                  </button>
                </div>
                {materiales.length === 0 ? (
                  <div style={{ textAlign:'center', padding:20, color:'#9CA3AF', fontSize:13 }}>Sin materiales agregados</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {materiales.map((m, i) => (
                      <div key={i} style={{ padding:'10px 12px', background:'#F4F6FA', borderRadius:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                          <span style={{ fontSize:14 }}>
                            {m.tipo==='nota' ? '📝' : m.tipo==='enlace' ? '🔗' : m.tipo==='archivo' ? '📎' : '📋'}
                          </span>
                          <span style={{ fontSize:13, fontWeight:600 }}>{m.titulo}</span>
                        </div>
                        {m.descripcion && <div style={{ fontSize:12, color:'#6B7280' }}>{m.descripcion}</div>}
                        {m.url && <a href={m.url} target='_blank' rel='noopener noreferrer' style={{ fontSize:12, color:'#3B82F6' }}>{m.url}</a>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal material */}
      {modal === 'material' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:480, width:'100%' }}>
            <h2 style={{ fontSize:18, fontWeight:800, marginBottom:20 }}>Agregar material</h2>
            <label style={labelStyle}>Tipo</label>
            <select style={inputStyle} value={form.tipo||'nota'} onChange={e => setForm(f => ({...f, tipo:e.target.value}))}>
              <option value='nota'>📝 Nota</option>
              <option value='enlace'>🔗 Enlace</option>
              <option value='archivo'>📎 Archivo</option>
              <option value='tarea'>📋 Tarea</option>
            </select>
            <label style={labelStyle}>Titulo</label>
            <input style={inputStyle} value={form.titulo||''} onChange={e => setForm(f => ({...f, titulo:e.target.value}))} placeholder="Titulo del material" />
            <label style={labelStyle}>Descripcion</label>
            <textarea style={{...inputStyle, minHeight:80, resize:'vertical'}} value={form.descripcion||''} onChange={e => setForm(f => ({...f, descripcion:e.target.value}))} placeholder="Descripcion o contenido..." />
            {(form.tipo==='enlace' || form.tipo==='archivo') && (
              <>
                <label style={labelStyle}>URL</label>
                <input style={inputStyle} value={form.url||''} onChange={e => setForm(f => ({...f, url:e.target.value}))} placeholder="https://..." />
              </>
            )}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => { setModal(null); setForm({}) }}
                style={{ padding:'10px 20px', background:'#F4F6FA', color:'#374151', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                Cancelar
              </button>
              <button onClick={guardarMaterial} disabled={guardando || !form.titulo}
                style={{ padding:'10px 24px', background: form.titulo ? '#3B82F6' : '#E2E6F0', color: form.titulo ? '#fff' : '#9CA3AF', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor: form.titulo ? 'pointer' : 'not-allowed' }}>
                {guardando ? 'Guardando...' : 'Agregar material'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nota estudiante */}
      {modal === 'nota' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:480, width:'100%' }}>
            <h2 style={{ fontSize:18, fontWeight:800, marginBottom:20 }}>Nota para estudiante</h2>
            <label style={labelStyle}>Estudiante</label>
            <select style={inputStyle} value={form.estudiante_id||''} onChange={e => setForm(f => ({...f, estudiante_id:e.target.value}))}>
              <option value=''>Seleccionar estudiante</option>
              {estudiantes.map((e,i) => (
                <option key={i} value={e.usuarios?.id}>{e.usuarios?.email}</option>
              ))}
            </select>
            <label style={labelStyle}>Nota</label>
            <textarea style={{...inputStyle, minHeight:100, resize:'vertical'}} value={form.nota||''} onChange={e => setForm(f => ({...f, nota:e.target.value}))} placeholder="Escribe tu nota sobre el desempeño del estudiante..." />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => { setModal(null); setForm({}) }}
                style={{ padding:'10px 20px', background:'#F4F6FA', color:'#374151', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                Cancelar
              </button>
              <button onClick={guardarNota} disabled={guardando || !form.estudiante_id || !form.nota}
                style={{ padding:'10px 24px', background: (form.estudiante_id && form.nota) ? '#059669' : '#E2E6F0',
                  color: (form.estudiante_id && form.nota) ? '#fff' : '#9CA3AF',
                  border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor: (form.estudiante_id && form.nota) ? 'pointer' : 'not-allowed' }}>
                {guardando ? 'Guardando...' : 'Guardar nota'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// El profesor edita su propia foto y biografia. El nombre y el correo
// se muestran de solo lectura -- esos los controla unicamente el
// administrador desde el Panel Admin.
function TarjetaMiPerfil({ perfil, onActualizado }) {
  const [editando, setEditando] = useState(false)
  const [bio, setBio] = useState(perfil.bio || '')
  const [subiendoFoto, setSubiendoFoto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  async function elegirFoto(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setSubiendoFoto(true)
    setError(null)
    try {
      const foto_url = await subirFotoProfesor(archivo)
      const r = await axios.put(API + '/curriculo/profesor/' + perfil.id + '/mi-perfil', { foto_url })
      onActualizado(r.data.datos)
    } catch (err) {
      setError('No se pudo subir la foto. Intenta de nuevo.')
    }
    setSubiendoFoto(false)
    e.target.value = ''
  }

  async function guardarBio() {
    setGuardando(true)
    setError(null)
    try {
      const r = await axios.put(API + '/curriculo/profesor/' + perfil.id + '/mi-perfil', { bio })
      onActualizado(r.data.datos)
      setEditando(false)
    } catch (err) {
      setError('No se pudo guardar. Intenta de nuevo.')
    }
    setGuardando(false)
  }

  return (
    <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:24, maxWidth:560, display:'flex', gap:18, alignItems:'flex-start', flexWrap:'wrap' }}>
      <div style={{ width:72, height:72, borderRadius:'50%', background:'#F4F6FA', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, overflow:'hidden', flexShrink:0 }}>
        {perfil.foto_url ? <img src={perfil.foto_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '👤'}
      </div>
      <div style={{ flex:1, minWidth:240 }}>
        <div style={{ fontSize:16, fontWeight:800, marginBottom:2 }}>{perfil.nombre} {perfil.apellido}</div>
        <div style={{ fontSize:12, color:'#9CA3AF', marginBottom:12 }}>
          Correo: {perfil.email} <span style={{ fontStyle:'italic' }}>(no se puede cambiar aquí)</span>
        </div>
        <label style={{ display:'inline-block', padding:'6px 12px', borderRadius:8, border:'1.5px solid #E2E6F0', background:'#fff', color:'#374151', fontSize:12, fontWeight:700, cursor:'pointer', marginBottom:14 }}>
          {subiendoFoto ? 'Subiendo...' : '📷 Cambiar mi foto'}
          <input type="file" accept="image/*" onChange={elegirFoto} disabled={subiendoFoto} style={{ display:'none' }} />
        </label>
        {!editando ? (
          <div>
            <p style={{ fontSize:13, color:'#374151', lineHeight:1.5, marginBottom:8 }}>{perfil.bio || 'Todavía no has escrito una biografía.'}</p>
            <button onClick={() => { setBio(perfil.bio || ''); setEditando(true) }}
              style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid #E2E6F0', background:'#fff', color:'#374151', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              ✏️ Editar biografía
            </button>
          </div>
        ) : (
          <div>
            <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Cuéntale al estudiante sobre ti..."
              style={{ width:'100%', minHeight:70, padding:'8px 10px', borderRadius:8, border:'1.5px solid #E2E6F0', fontFamily:'inherit', fontSize:13, boxSizing:'border-box', resize:'vertical', marginBottom:8 }} />
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={guardarBio} disabled={guardando}
                style={{ padding:'6px 14px', borderRadius:8, border:'none', background:'#059669', color:'#fff', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
              <button onClick={() => setEditando(false)}
                style={{ padding:'6px 14px', borderRadius:8, border:'1.5px solid #E2E6F0', background:'#fff', color:'#374151', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
        {error && <p style={{ fontSize:12, color:'#991B1B', marginTop:8 }}>{error}</p>}
      </div>
    </div>
  )
}

function TarjetaClase({ clase, hoy, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background:'#fff', border:'1.5px solid ' + (hoy ? '#EF4444' : '#E2E6F0'),
        borderRadius:14, padding:'16px 20px', cursor:'pointer', transition:'all .15s ease',
        boxShadow: hoy ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none' }}
      onMouseOver={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.08)' }}
      onMouseOut={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow= hoy ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none' }}>
      <div style={{ display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ textAlign:'center', minWidth:70 }}>
          <div style={{ fontSize:18, fontWeight:800, color: hoy ? '#EF4444' : '#1B3A8C' }}>
            {clase.hora_inicio ? (parseInt(clase.hora_inicio.split(':')[0]) % 12 || 12) + ':' + clase.hora_inicio.split(':')[1] + (parseInt(clase.hora_inicio.split(':')[0]) >= 12 ? ' PM' : ' AM') : ''}
          </div>
          <div style={{ fontSize:11, color:'#6B7280' }}>{clase.duracion_min || 60} min</div>
        </div>
        <div style={{ width:1, height:44, background:'#E2E6F0' }} />
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <span style={{ background:'#1B3A8C', color:'#fff', fontSize:10, fontWeight:800, padding:'1px 7px', borderRadius:999 }}>{clase.nivel_id}</span>
            {hoy && <span style={{ background:'#FEF2F2', color:'#EF4444', fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:999 }}>HOY</span>}
            <span style={{ fontSize:14, fontWeight:700, color:'#1A1A2E' }}>{clase.nombre}</span>
          </div>
          {clase.tema && <div style={{ fontSize:12, color:'#7C3AED' }}>📌 {clase.tema}</div>}
          <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>{formatFecha(clase.fecha)}</div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#22C55E' }}>{clase.cupos_total - (clase.cupos_disponibles || 0)}/{clase.cupos_total}</div>
          <div style={{ fontSize:10, color:'#6B7280' }}>inscritos</div>
          <div style={{ fontSize:11, color:'#3B82F6', marginTop:4 }}>Ver detalles →</div>
        </div>
      </div>
    </div>
  )
}
